import { Any } from '@policyfly/protobuf/google/protobuf'
import { BadRequest, Status } from '@policyfly/protobuf/google/rpc'
import { GrpcStatusCode } from '@protobuf-ts/grpcweb-transport'
import { base64decode } from '@protobuf-ts/runtime'
import { RpcError } from '@protobuf-ts/runtime-rpc'
import { isAxiosError } from 'axios'

import type { AnyObject } from '@policyfly/types/common'
import type { RpcMetadata } from '@protobuf-ts/runtime-rpc'

/**
 * Converts a JS object to a {@link FormData} representation.
 */
export function objectToFormData (obj: AnyObject, formData = new FormData()): FormData {
  for (const [k, v] of Object.entries(obj)) {
    if (v instanceof Blob) {
      formData.append(k, v)
    } else if (typeof v === 'object' || Array.isArray(v)) {
      formData.append(k, JSON.stringify(v))
    } else {
      formData.append(k, v)
    }
  }
  return formData
}

/**
 * Converts a base64 string (without prefix) to a {@link Uint8Array}.
 *
 * @example
 * const base64str = Application.policy_state
 * const uintArr = base64ToUint8Array(base64str)
 * const policyState = PolicyState.fromBinary(uintArr)
 */
export function base64ToUint8Array (base64: string): Uint8Array {
  return Uint8Array.from(window.atob(base64), (v) => v.charCodeAt(0))
}

/**
 * Converts a {@link Blob} to a base64 string (without prefix).
 *
 * @example
 * const uintArr = PolicyState.toBinary(policyState)
 * const blob = new Blob([uintArr])
 * const base64str = await blobToBase64(blob)
 */
export function blobToBase64 (blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const result = reader.result as string
      const base64 = result.substring(result.indexOf(',') + 1)
      resolve(base64)
    }
    reader.onerror = (err) => reject(err)
    reader.readAsDataURL(blob)
  })
}

/**
 * Checks if the provided error is an AbortError.
 * This is thrown when an AbortController is used to cancel a request.
 * Handles both normal fetch and GRPC errors.
 */
export function isAbortError (error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return true
  if (error instanceof RpcError) {
    if (error.code === GrpcStatusCode[GrpcStatusCode.CANCELLED]) return true
    if (error.message === GrpcStatusCode[GrpcStatusCode.CANCELLED]) return true
  }
  return false
}

/**
 * The gRPC code of each HTTP status a server can return for a failed gRPC call.
 * Where several codes share a status, the most likely one is used.
 */
const HTTP_STATUS_GRPC_CODES: Partial<Record<number, GrpcStatusCode>> = {
  400: GrpcStatusCode.INVALID_ARGUMENT,
  401: GrpcStatusCode.UNAUTHENTICATED,
  403: GrpcStatusCode.PERMISSION_DENIED,
  404: GrpcStatusCode.NOT_FOUND,
  409: GrpcStatusCode.ALREADY_EXISTS,
  429: GrpcStatusCode.RESOURCE_EXHAUSTED,
  499: GrpcStatusCode.CANCELLED,
  500: GrpcStatusCode.INTERNAL,
  501: GrpcStatusCode.UNIMPLEMENTED,
  503: GrpcStatusCode.UNAVAILABLE,
  504: GrpcStatusCode.DEADLINE_EXCEEDED,
}

/**
 * Converts the failed response of an HTTP route on a gRPC server to an {@link RpcError},
 * so it is handled the same as a failed gRPC call.
 *
 * The response body is used as the error message, and its status as the gRPC code.
 */
export async function httpResponseToRpcError (response: Response): Promise<RpcError> {
  const code = HTTP_STATUS_GRPC_CODES[response.status] ?? GrpcStatusCode.UNKNOWN
  const message = await response.text().catch(() => '')
  return new RpcError(message || response.statusText, GrpcStatusCode[code])
}

/**
 * Retrieve an error message from a Django or gRPC api call failure.
 *
 * Falls back to provided string if no error string can be extracted.
 * Returns an empty string if no fallback is provided, and no error can be extracted.
 */
export function extractApiError (error: unknown, fallback?: string): string {
  switch (true) {
    case isAxiosError(error): {
      // extract the first error from either the data or non_field_errors
      if (Array.isArray(error.response?.data)) {
        return error.response?.data[0]
      } else if (Array.isArray(error.response?.data?.non_field_errors)) {
        return error.response.data.non_field_errors[0]
      }
      break
    }
    case error instanceof RpcError: {
      const message = (decodeURIComponent(error.message) ?? '')
        .replace(/^\[[a-z-_]+\/[a-z-_]+\]/, '')
        .trim()
      if (message) return message
    }
  }
  return fallback ?? ''
}

/**
 * Extracts a "rich" error from a gRPC metadata object.
 * If possible returns it as a google {@link Status}.
 *
 * See https://google.aip.dev/193#http11json-representation for more details.
 */
export function extractRichError (metadata: RpcMetadata): Status | null {
  const statusBin = metadata['grpc-status-details-bin']
  if (!statusBin) return null
  const b64StatusBin = typeof statusBin === 'string' ? statusBin : statusBin[statusBin.length - 1]
  if (!b64StatusBin) return null
  return Status.fromBinary(base64decode(b64StatusBin))
}

/**
 * Extract field violations from a gRPC error
 *
 * Returns an object mapping the field id/name to the violation description.
 */
export function extractFieldViolations (error: unknown): { [uuid4: string]: string } {
  if (error instanceof RpcError) {
    const status = extractRichError(error.meta)
    if (status) {
      const violations = status.details
        .flatMap((detail) => {
          if (!Any.contains(detail, BadRequest)) return []
          return Any.unpack(detail, BadRequest).fieldViolations
        })
        .reduce((acc, violation) => ({ ...acc, [violation.field]: violation.description }), {})
      return violations
    }
  }
  return {}
}

/**
 * Converts bytes to an object url that can be displayed in the browser or downloaded.
 * A mimeType can also be provided.
 *
 * **NOTE**: The url holds the bytes alive until it is revoked.
 * It is the caller's responsibility to release it when no longer needed.
 */
export function bytesToObjectURL (bytes: string | Uint8Array, mimeType?: string): string {
  const blob = new Blob([bytes as BlobPart], { type: mimeType })
  return URL.createObjectURL(blob)
}
